import { DateTime } from "luxon";
import { observable, action, computed, runInAction } from "mobx";
import {
	AccountTypes,
	UserTypes,
	ISOToDisplayCode,
	getUserFromLocalStorage,
	isGamesTheme,
	getUserToken,
	retryRequest,
} from "@utils";
import { UserAccount } from "@data-types/user";
import {
	AccountVerificationStatus,
	LoginHistoryResponseModel,
} from "@api-types/user/LoginResponseDto";

import { localizationService, SessionStorageProvider } from "@state";

import { BettingActivityStore } from ".";
import {
	LoginResponseUser,
	LoginResponseToken,
	LoginActivationResponseDto,
} from "@api-types/user/LoginResponseDto";
import KeepAliveApiService from "@api-services/platform/KeepAliveApiService";
import { LogoutReason } from "@api-types/user/LoginRequestDto";
import { UserAccountType } from "@common/constants/UserAccountType";

import { getCurrentCulture } from "@utils";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";

import { StorageStateKeysEnum } from "@utils/storage";
import { BonusStatusEnum } from "@api-types/my-bonuses/Bonus";
import { PushNotificationsHandler } from "@utils/specific/capacitor/push-notifications/PushNotificationsHandler";
import UserApiService from "@api-services/user/UserApiService";
import type { RootStore } from "@state/stores/types";
import { THEME_SETTINGS } from "@theme-settings";
import { analyticsTracker } from "@utils/analytics";
import { LoginStore } from "@state/auth";
import { AccountActivationStore } from "@state/auth/AccountActivationStore";
import { PasswordResetStore } from "@state/auth/PasswordResetStore";
import { UserDataConfirmationStore } from "@state/auth/UserDataConfirmationStore";
import { UserMatchingStore } from "@state/auth/UserMatchingStore";
import { logger } from "@utils/logger/logger";
import { ShopUserShop } from "@data-types/common/ShopUserShops";
import { RequestContextType } from "@utils/api-config/RequestContext";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";
import { MarketplaceBonusEligibilityApiService } from "@api-services/my-bonuses/MarketplaceBonusEligibilityApiService";
import { ContextOptions } from "@utils/api-config/types";
import { ONLINE_USER_WALLETS } from "@common/constants/wallets/OnlineUserWallets";
import { RETAIL_USER_WALLETS } from "@common/constants/wallets/RetailUserWallets";
import { isWalletDisabledForBetting } from "@utils/common/isWalletDisabledForBetting";
import { BETTING_USER_WALLETS } from "@common/constants/wallets/BettingUserWallets";

const loadFailPath = `/${getCurrentCulture()}/app-update`;
// prettier-ignore
const UserService = lazy(loadFailPath, ()=>import("@services/user/UserService"));

export class UserAuthStore {
	rootStore: RootStore;
	private keepAliveInterval: number | undefined;
	bettingActivityStore: BettingActivityStore;
	@observable userAccounts: UserAccount[] | null = observable.array();
	@observable user: User | null = null;
	@observable secondaryUser: User | null = null;
	@observable token: UserToken | null = null;
	@observable secondaryToken: UserToken | null = null;

	@observable accountActivation: AccountActivation | null = null;
	@observable loginHistory: LoginHistoryResponseModel | null = null;
	@observable activeAccountTypeId: string | null = null;
	@observable activeWalletId: string | null = null;

	@observable activeShopUserShopId: string | null = null;

	@observable lastTimeChangedTokenExpiration: string | null = null;
	@observable lastTimeChangedSecondaryTokenExpiration: string | null = null;

	@observable realityCheckTime: number | null = null;
	@observable autoLogoutPeriod: number | null = null;

	public loginStore: LoginStore;
	public accountActivationStore: AccountActivationStore;
	public passwordResetStore: PasswordResetStore;
	public userDataConfirmationStore: UserDataConfirmationStore;
	public userMatchingStore: UserMatchingStore;

	//#region user field getters

	// TODO: why would any method return boolean || enum value ???
	@computed public get activeUserWallet() {
		if (!this.isLoggedIn) {
			return false;
		}
		const userType = this.getWalletOwnerByWalletId(this.activeWalletId);

		return userType;
	}

	@computed public get isUserCritical(): boolean {
		if (!this.isLoggedIn) {
			return false;
		}

		const onlineUser = getUserFromLocalStorage(
			this.isPrimaryUserOnline == true
				? UserTypes.PRIMARY
				: UserTypes.SECONDARY
		);

		if (
			this.isInternetUser &&
			THEME_SETTINGS.isAccountVerificationSectionEnabled
		) {
			if (
				onlineUser?.accountVerificationStatus !==
					AccountVerificationStatus.FullyVerified &&
				!this.user?.isRegisteredWithExternalProvider
			) {
				return true;
			}
		}

		if (!isGamesTheme()) {
			if (this.secondaryUser) {
				if (onlineUser?.depositLimitUpdate) {
					return true;
				}
			} else if (this.user && this.user?.depositLimitUpdate) {
				return true;
			}
		}

		const userType = this.getWalletOwnerByWalletId(this.activeWalletId);

		const user =
			UserTypes.PRIMARY == userType ? this.user : this.secondaryUser;

		if (user?.isBettingDisabled && !isGamesTheme()) {
			return true;
		}

		if (this.user?.isAnnualIncomeRequired) {
			return true;
		}

		if (!isGamesTheme()) {
			const expirationDate =
				this.user?.userAccountDeactivationGracePeriodExpirationDate;
			if (
				expirationDate &&
				DateTime.fromISO(expirationDate) < DateTime.now()
			) {
				return true;
			}
			const secondaryExpirationDate =
				this.secondaryUser
					?.userAccountDeactivationGracePeriodExpirationDate;
			if (
				secondaryExpirationDate &&
				DateTime.fromISO(secondaryExpirationDate) < DateTime.now()
			) {
				return true;
			}
		}

		return false;
	}

	@computed public get isUserWarning(): boolean {
		if (!this.isLoggedIn) {
			return false;
		}

		if (!isGamesTheme()) {
			if (
				(this.user && this.user.userDocumentDateExpired) ||
				this.user?.userDocumentDateExpiresSoon ||
				this.user?.userDocumentDateNotSet
			) {
				return true;
			}
		}

		if (!isGamesTheme()) {
			if (
				(this.secondaryUser &&
					this.secondaryUser.userDocumentDateExpired) ||
				this.secondaryUser?.userDocumentDateExpiresSoon ||
				this.secondaryUser?.userDocumentDateNotSet
			) {
				return true;
			}
		}

		if (!isGamesTheme()) {
			const expirationDate =
				this.user?.userAccountDeactivationGracePeriodExpirationDate;
			if (
				expirationDate &&
				DateTime.fromISO(expirationDate) >= DateTime.now()
			) {
				return true;
			}
			const secondaryExpirationDate =
				this.secondaryUser
					?.userAccountDeactivationGracePeriodExpirationDate;
			if (
				secondaryExpirationDate &&
				DateTime.fromISO(secondaryExpirationDate) >= DateTime.now()
			) {
				return true;
			}
		}

		return false;
	}

	@computed public get getUserId(): string {
		const userId = this.onlineUser?.id ?? this.user?.id;

		return userId ?? "";
	}

	@computed public get isRefreshButtonDisabled(): boolean {
		const lastRefresh = DateTime.fromISO(
			this.user?.lastRefreshTime as string
		).toUTC();

		const currentTime = DateTime.now().toUTC();

		return (
			lastRefresh.plus({
				hours: 1,
			}) >= currentTime
		);
	}

	@computed get isMultiUser(): boolean {
		return !!(
			this.user &&
			this.secondaryUser &&
			this.token &&
			this.secondaryToken
		);
	}

	@computed get isMultiCardUser(): boolean {
		if (!this.shopUserWallets?.length) {
			return false;
		}
		return this.shopUserShops.size > 1;
	}

	@computed get isTWDKMultiCardUser(): boolean {
		if (!this.onlineUserWallets?.length) {
			return false;
		}
		return this.onlineUserShops.size > 1;
	}

	@computed public get availableWallets() {
		return this.userAccounts?.filter(
			(wallet) =>
				wallet.userId === this.user?.id ||
				wallet.userId === this.secondaryUser?.id
		);
	}

	@computed public get availableUserWalletsIds() {
		const ALLOWED_FILTER_WALLET_OPTIONS = new Set([
			UserAccountType.SportBettingAccountOnline,
			UserAccountType.SportBettingAccountShopOnline,
			UserAccountType.SportBettingAccountShop,
			UserAccountType.CasinoAccount,
			UserAccountType.LiveCasinoAccount,
			UserAccountType.Games,
		]);
		return this.availableWallets
			?.filter((wallet) => ALLOWED_FILTER_WALLET_OPTIONS.has(wallet.abrv))
			?.map((wallet) => wallet.accountTypeId);
	}

	@computed public get isAutoWithdrawalEnabled() {
		return (
			(this.user && this.user.isAutoWithdrawalEnabled) ||
			(this.secondaryUser && this.secondaryUser.isAutoWithdrawalEnabled)
		);
	}

	@computed public get isPrimaryUserOnline(): boolean {
		return this.user?.isInternetUser === true;
	}

	@computed public get isInternetUser(): boolean {
		return (
			(this.user != null && this.user.isInternetUser) ||
			(this.secondaryUser != null && this.secondaryUser.isInternetUser)
		);
	}

	@computed public get isLogoutDisabled(): boolean {
		return this.rootStore.betSlipStore?.isCountdownVisible;
	}

	@computed public get agencyKey(): string {
		return (
			this.user?.agencyKey ||
			this.accountActivation?.agencyKey ||
			WEBPACK_DEFAULT_AGENCY_KEY
		);
	}
	@computed public get secondaryAgencyKey(): string {
		return this.secondaryUser?.agencyKey || WEBPACK_DEFAULT_AGENCY_KEY;
	}

	@computed public get shopId(): string {
		return this.user?.shopId || WEBPACK_DEFAULT_SHOP_ID;
	}
	@computed public get secondaryShopId(): string {
		return this.secondaryUser?.shopId || WEBPACK_DEFAULT_SHOP_ID;
	}

	@computed public get agencyId(): string {
		return this.user?.agencyId || this.accountActivation?.agencyId || "";
	}

	@computed public get secondaryAgencyId(): string {
		return this.secondaryUser?.agencyId || "";
	}

	@computed public get isLoggedIn(): boolean {
		return this.token != null && this.user != null;
	}

	@computed public get onlineUserToken(): string | null {
		let tokenData = null;

		if (this.isPrimaryUserOnline) {
			tokenData = getUserToken(UserTypes.PRIMARY);
		} else {
			tokenData = getUserToken(UserTypes.SECONDARY);
		}

		if (tokenData) {
			return tokenData.token;
		}

		return null;
	}

	@computed public get shopUserToken(): string | null {
		let tokenData = null;

		if (this.isPrimaryUserOnline) {
			tokenData = getUserToken(UserTypes.SECONDARY);
		} else {
			tokenData = getUserToken(UserTypes.PRIMARY);
		}

		if (tokenData) {
			return tokenData.token;
		}

		return null;
	}

	@computed public get onlineUser(): User | null {
		if (this.user?.isInternetUser) {
			return this.user;
		}
		if (this.secondaryUser?.isInternetUser) {
			return this.secondaryUser;
		}
		return null;
	}

	@computed public get shopUser(): User | null {
		if (!this.user?.isInternetUser) {
			return this.user;
		}
		if (!this.secondaryUser?.isInternetUser) {
			return this.secondaryUser;
		}
		return null;
	}

	/**
	 * True if there is any user data present. Not all observables are loaded at once.
	 * They are loaded one by one local storage key.
	 */
	@computed public get isPartiallyLoggedIn(): boolean {
		return (
			this.token != null ||
			this.user != null ||
			this.accountActivation != null
		);
	}

	@computed public get currency(): string {
		if (this.user == null) {
			return ISOToDisplayCode(THEME_SETTINGS.defaultCurrency);
		} else {
			if (this.userAccounts != null) {
				const foundAccount = this.userAccounts.find(
					(acc) => acc.id === this.activeWalletId
				);

				if (foundAccount != null) {
					return foundAccount.currency.displayCode;
				}

				return ISOToDisplayCode(THEME_SETTINGS.defaultCurrency);
			}

			return ISOToDisplayCode(THEME_SETTINGS.defaultCurrency);
		}
	}

	/**
	 *  if there is not online user this will be empty array  */
	@computed public get onlineUserWallets() {
		if (!this.onlineUser || !this.userAccounts?.length) {
			return [];
		}
		return this.userAccounts?.filter((wallet) =>
			ONLINE_USER_WALLETS.has(wallet.abrv)
		);
	}

	/**
	 *  if there is not shop user this will be empty array  */
	@computed public get shopUserWallets() {
		if (!this.shopUser || !this.userAccounts?.length) {
			return [];
		}

		return this.userAccounts?.filter((wallet) =>
			RETAIL_USER_WALLETS.has(wallet.abrv)
		);
	}

	@computed
	public get walletsByUserId() {
		const wallets: Record<string, UserAccount[]> = {};
		const onlineUser = this.onlineUser;
		const shopUser = this.shopUser;

		if (onlineUser) {
			wallets[onlineUser.id] = this.onlineUserWallets;
		}
		if (shopUser) {
			wallets[shopUser.id] = this.shopUserWallets;
		}
		return wallets;
	}

	@computed public get onlineUserShops() {
		const map = new Map<string, ShopUserShop>();

		this.onlineUserWallets.forEach((wallet) => {
			if (!map.has(wallet.shopId)) {
				map.set(wallet.shopId, {
					displayName: wallet.displayName,
					shopId: wallet.shopId,
					wallets: [],
					isShopDisabled: false,
				});
			}
			map.get(wallet.shopId)!.wallets.push(wallet);
		});

		return map;
	}

	@computed public get shopUserShops() {
		const map = new Map<string, ShopUserShop>();
		this.shopUserWallets.forEach((wallet) => {
			if (!map.has(wallet.shopId)) {
				map.set(wallet.shopId, {
					displayName: wallet.displayName,
					shopId: wallet.shopId,
					wallets: [],
					isShopDisabled: false,
				});
			}
			map.get(wallet.shopId)!.wallets.push(wallet);
		});
		map.forEach((shopUsersShop) => {
			if (shopUsersShop.wallets.length > 0) {
				shopUsersShop.isShopDisabled = shopUsersShop.wallets.every(
					(wallet) =>
						isWalletDisabledForBetting(wallet) ||
						wallet.abrv == UserAccountType.SportBettingAccountShop
				);
			}
		});
		return map;
	}
	/**
	 * This will have default value for shopId
	 * depending of theme
	 * TWDK has his own handling
	 * Rest of the themes have same shopId same as user
	 */
	@computed public get defaultOnlineShopId() {
		if (THEME_SETTINGS.isDKMW && this.userAccounts) {
			const onlineWallet = this.userAccounts.find(
				(wallet) =>
					wallet.isPrimary &&
					[UserAccountType.SportBettingAccountOnline].includes(
						wallet.abrv
					)
			);
			return onlineWallet?.shopId;
		}

		if (this.onlineUser) {
			return this.onlineUser.shopId;
		}
		// Case when you do not have user but have it's wallets compute shopId if needed
		if (this.userAccounts) {
			const onlineWallet = this.userAccounts.find((wallet) =>
				ONLINE_USER_WALLETS.has(wallet.abrv)
			);
			if (onlineWallet) {
				return onlineWallet.shopId;
			}
		}
		return null;
	}

	@computed
	get shouldDisplayActivateWallet() {
		return (
			this.userAccounts?.some(
				(w) => w.abrv == UserAccountType.SportBettingAccountOnline
			) &&
			this.userAccounts?.some(
				(w) =>
					w.abrv == UserAccountType.CashToDigitalAccount ||
					w.abrv == UserAccountType.SportBettingAccountShopOnline
			)
		);
	}
	//#endregion user field getters

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;

		this.bettingActivityStore = new BettingActivityStore(this);
		this.loginStore = new LoginStore(this);
		this.accountActivationStore = new AccountActivationStore(this);
		this.passwordResetStore = new PasswordResetStore(this);
		this.userDataConfirmationStore = new UserDataConfirmationStore(this);
		this.userMatchingStore = new UserMatchingStore(this);

		clearInterval(this.keepAliveInterval);
		this.keepAliveInterval = Number(
			setInterval(
				this.handleKeepAlive,
				THEME_SETTINGS.keepAliveInterval * 1000
			)
		);
	}

	private shouldLogoutCurrentUser(newUserName: string): boolean {
		if (!this.user) {
			return false;
		}
		if (this.user.username == newUserName) {
			return false;
		}
		if (this.secondaryUser && this.secondaryUser.username == newUserName) {
			return false;
		}
		return true;
	}

	/* initialize user data  */
	@action.bound
	async onInitialize(): Promise<void> {
		const searchParams = new URLSearchParams(location.search);
		const tokenValue = searchParams.get("gotsso");
		await this.readFromLocalStorage(null);
		if (tokenValue) {
			try {
				const loginResponse =
					await UserApiService.gamesSsoLogin(tokenValue);
				if (
					this.shouldLogoutCurrentUser(
						loginResponse.primary.user.username
					)
				) {
					await this.logoutUser();
				}
				await this.login(loginResponse);
			} catch (error) {
				if (error?.data?.errorCode == "40414") {
					console.error(error);
					this.rootStore.notificationStore.error(
						localizationService.t(
							"ERR_HANDLING.GAMES_NOT_ALLOWED_ERROR"
						)
					);
				}
			}
		}
		await this.readFromLocalStorage(null);
		window.addEventListener("storage", this.storageActionListener, {});

		//if user accounts don't contain user id, refresh user balance
		if (this.user != null && this.userAccounts == null) {
			await this.refreshUserBalance();
		}

		// user matching data preparation if user matching process is active
		await this.userMatchingStore.checkUserMatchingState();
	}

	@action.bound
	syncToken(): void {
		if (this.user != null && this.token != null) {
			const currentDateTime = DateTime.utc().toMillis();
			const storageTokenTimeInMillis = DateTime.fromISO(
				this.token.expirationDate
			).toMillis();

			if (
				currentDateTime >= storageTokenTimeInMillis &&
				!this.rootStore.sessionExpireStore.isGameOpen
			) {
				this.removeUserFromLocalStorage();
			} else {
				this.setLastTimeChangedTokenExpiration(DateTime.utc().toISO());
				const newTokenExpirationDate = DateTime.utc().plus(
					this.token.slidingWindow * 1000
				);
				this.setTokenExpirationDate(
					newTokenExpirationDate.toISO() as string
				);
			}
		}
	}

	@action.bound
	removeUserFromLocalStorage(): void {
		const wasLoggedIn = this.isLoggedIn;

		if (this.rootStore.chatViewStore.chatClient) {
			//triggering this because of 401s, token expired, automatic logout, etc, where logout is not called
			this.rootStore.chatViewStore.updateChatOnLoginChange(false);
		}

		this.rootStore.cashoutHubStore.destroyConnectionOnUserLogout();
		this.rootStore.userHubStore.destroyConnectionOnUserLogout();
		this.rootStore.scorePredictorHub.destroyConnectionOnUserLogout();

		// clear state
		this.bettingActivityStore.bettingActivityFetchedData = null;
		this.bettingActivityStore.bettingActivityError = null;

		this.setUser(null);
		this.setToken(null);

		this.setSecondaryUser(null);
		this.setSecondaryToken(null);

		this.setAuthenticationToken(null);

		this.setLoginHistory(null);
		this.setUserAccounts(null);
		this.setActiveShopUserShopId(null);

		this.setLastTimeChangedTokenExpiration(null);
		this.setLastTimeChangedSecondaryTokenExpiration(null);

		this.setRealityCheckTime(null);
		this.setAutoLogoutPeriod(null);

		this.setActiveUserAccountTypeId(null);
		this.setActiveWalletId(null);

		this.userMatchingStore.setUserMatchingState(false);
		this.loginStore.setUserDataConfirmationState(false);
		this.accountActivationStore.setUserConfirmationData(null);
		this.removeAccountStatementFilter();
		this.removeMyBetsFilter();
		// clear local storage
		this.rootStore.localStorageProvider.remove(StorageStateKeysEnum.SLN);
		this.removeInsicTokensFromLocalStorage();
		this.rootStore.gamePauseStore.disposeTimerData();

		if (wasLoggedIn) {
			analyticsTracker.trackLogout();
		}
	}

	@action.bound
	removeInsicTokensFromLocalStorage(): void {
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			// Check if the key contains the word "avs"
			if (key?.includes("avs-")) {
				localStorage.removeItem(key);
			}
		}
	}

	@action.bound
	setAcceptGdprFlag(): void {
		runInAction(() => {
			if (this.user != null) {
				this.user.shouldAcceptGdpr = false;
				this.setUser(this.user);
			}
		});
	}

	@action.bound
	updateUserChatName(chatName: string): void {
		if (this.user == null) {
			return;
		}
		this.user.chatName = chatName;
		this.setUser(this.user);
	}

	@action.bound
	setIsCreditRatingCheckRequired(): void {
		if (this.user == null) {
			return;
		}
		this.user.isCreditRatingCheckRequired = false;
		this.setUser(this.user);
	}

	@action.bound
	updateUserVerificationStatusToFullyVerified(isVerified: boolean): void {
		if (this.user == null) {
			return;
		}

		const onlineUser = this.isPrimaryUserOnline
			? UserTypes.PRIMARY
			: UserTypes.SECONDARY;

		const shouldUpdatePrimary =
			!this.secondaryUser || onlineUser === UserTypes.PRIMARY;

		if (
			isVerified &&
			this.user.accountVerificationStatus !==
				AccountVerificationStatus.FullyVerified
		) {
			if (shouldUpdatePrimary) {
				this.user.accountVerificationStatus =
					AccountVerificationStatus.FullyVerified;
				this.setUser(this.user);
			} else if (this.secondaryUser) {
				this.secondaryUser.accountVerificationStatus =
					AccountVerificationStatus.FullyVerified;
				this.setSecondaryUser(this.secondaryUser);
			}
		}

		if (
			!isVerified &&
			this.user.accountVerificationStatus ===
				AccountVerificationStatus.FullyVerified
		) {
			if (shouldUpdatePrimary) {
				this.user.accountVerificationStatus =
					AccountVerificationStatus.Unverified;
				this.setUser(this.user);
			} else if (this.secondaryUser) {
				this.secondaryUser.accountVerificationStatus =
					AccountVerificationStatus.Unverified;
				this.setSecondaryUser(this.secondaryUser);
			}
		}
	}

	@action.bound
	async checkBalanceEntry(): Promise<void> {
		if (this.user && this.activeWalletId?.includes("object")) {
			await this.refreshUserBalance();
		}
	}

	@action.bound
	async refreshUserBalance(): Promise<void> {
		if (!this.user?.id) {
			return;
		}

		let userWallets: UserAccount[] = [];
		try {
			userWallets = await (await UserService).default.getUserAccounts();
		} catch (e) {
			console.warn(e);
		}

		await this.setUserAccounts(userWallets);
	}

	@action.bound
	async login(userData: {
		primary: {
			user: LoginResponseUser;
			token: UserToken;
			accountActivation?: AccountActivation | null;
			loginHistory: LoginHistoryResponseModel;
			userAccounts: UserAccount[];
		};
		secondary?: {
			user: LoginResponseUser;
			token: UserToken;
		};
	}) {
		this.rootStore.betSlipStore.savedSlipStore.clearAnonReadyBet();

		// primary account
		this.setToken(userData.primary.token);

		this.setUser({
			...userData.primary.user,
			userContextType: RequestContextType.Default,
		});
		this.setAuthenticationToken(userData.primary.accountActivation);

		if (!this.user?.isCustomWebRole) {
			this.setLoginHistory(userData.primary.loginHistory);

			this.rootStore.chatViewStore.updateChatOnLoginChange(
				this.isLoggedIn
			);
			//secondary account

			let secondaryUserToSet = null;

			if (userData.secondary?.user) {
				secondaryUserToSet = {
					...userData.secondary.user,
					userContextType: userData.secondary.user.isInternetUser
						? RequestContextType.Online
						: RequestContextType.Shop,
				};
			}
			this.setSecondaryUser(secondaryUserToSet);

			await this.setUserAccounts(userData.primary.userAccounts);
			this.setLastTimeChangedTokenExpiration(new Date().toISOString());

			if (userData.secondary?.token != null) {
				this.setSecondaryToken(userData.secondary.token);
			}
		}

		if (userData.primary.user) {
			if (
				userData.primary.user.autoLogoutPeriod != null &&
				userData.primary.user.autoLogoutPeriod > 0
			) {
				const currentDate = new Date();
				this.setAutoLogoutPeriod(
					currentDate.getTime() +
						userData.primary.user.autoLogoutPeriod * 60 * 60 * 1000
				);
			}

			if (THEME_SETTINGS.isRealityCheckEnabled) {
				this.setRealityCheckTime(
					this.rootStore.realityCheckStore
						.realityCheckIntervalDuration
				);
			}
		}

		if (this.rootStore.betSlipStore.isSlipEmpty) {
			this.rootStore.betSlipStore.onResetSlip();
		}

		await this.rootStore.betSlipStore.refreshFreeBets();

		this.rootStore.cashoutHubStore.initializeCashoutHub();
		this.rootStore.userHubStore.initializeUserHub();

		this.rootStore.initClick();
		this.rootStore.welcomeStore.setShouldDisplay();

		PushNotificationsHandler.updateUserToken();

		this.rootStore.realityCheckStore.initializeRealityCheck();

		this.rootStore.notificationFavoritesStore.getUserNotificationFavorites();
		logger.setUserContext(this);
	}

	sortUserAccounts = (userAccounts: UserAccount[]): UserAccount[] => {
		const sortOrderOfAccounts = [
			UserAccountType.SportBettingAccountOnline,
			UserAccountType.SportBettingAccountShopOnline,
			UserAccountType.CashToDigitalAccount,
			UserAccountType.CasinoAccount,
			UserAccountType.LiveCasinoAccount,
			UserAccountType.SportBettingAccountShop,
			UserAccountType.Games,
		];

		return userAccounts.sort(
			(a, b) =>
				sortOrderOfAccounts.indexOf(a.abrv) -
				sortOrderOfAccounts.indexOf(b.abrv)
		);
	};

	getUserTypeFromBettingAccountTypeId(bettingAccountTypeId: string) {
		if (
			this.secondaryUser?.id ===
			this.userAccounts?.find(
				(acc) => bettingAccountTypeId === acc.accountTypeId
			)?.userId
		) {
			return UserTypes.SECONDARY;
		} else {
			return UserTypes.PRIMARY;
		}
	}

	getUserFromBettingAccountTypeId(bettingAccountTypeId: string) {
		if (
			this.secondaryUser?.id ===
			this.userAccounts?.find(
				(acc) => bettingAccountTypeId === acc.accountTypeId
			)?.userId
		) {
			return this.secondaryUser;
		}
		return this.user;
	}

	@action.bound
	async logoutUser(reasonsObj?: LogoutReason): Promise<void> {
		const finalReasonObj: LogoutReason = {
			isTimeoutLogout: false,
			isAutomaticLogout: false,
			isRealityCheckLogout: false,
			...reasonsObj,
		};

		await this.rootStore.chatViewStore.updateChatOnLoginChange(false);

		await (await UserService).default.logoutUser(finalReasonObj);

		this.removeUserFromLocalStorage();

		this.rootStore.welcomeStore.onDispose();

		SessionStorageProvider.remove("slip");

		if (this.rootStore) {
			this.rootStore.betSlipStore.onResetSlip();
		}

		if (this.rootStore.betSlipStore.deactivateOneClickBet) {
			this.rootStore.betSlipStore.deactivateOneClickBet();
		}
		if (this.rootStore.betSlipStore.resetBetSlipTypeToDefault) {
			this.rootStore.betSlipStore.resetBetSlipTypeToDefault();
		}

		this.rootStore.betSlipStore.clearFreebetOnLogout();

		this.loginStore.isSSOLoginInitialized = false;
		this.rootStore.cashoutHubStore.destroyConnectionOnUserLogout();
		this.rootStore.userHubStore.destroyConnectionOnUserLogout();
		this.rootStore.scorePredictorHub.destroyConnectionOnUserLogout();

		this.rootStore.realityCheckStore.onDispose();

		this.rootStore.notificationFavoritesStore.onDispose();
		logger.setUserContext();
	}

	handleKeepAlive = async (): Promise<void> => {
		if (this.isLoggedIn) {
			if (this.token != null) {
				await this.keepAlive(
					createApiRequestOptions(RequestContextType.Default)
				);
				this.syncToken();

				if (this.secondaryToken != null) {
					const currentDate = new Date();
					const newTokenExpirationDate = DateTime.utc().plus(
						this.secondaryToken.slidingWindow * 1000
					);
					const newSecondaryTokenData = {
						...this.secondaryToken,
						expirationDate:
							newTokenExpirationDate.toISO() as string,
					};
					const secondaryContext = this.rootStore.userAuthStore
						?.secondaryUser?.isInternetUser
						? RequestContextType.Online
						: RequestContextType.Shop;
					this.keepAlive(createApiRequestOptions(secondaryContext));
					this.setSecondaryToken(newSecondaryTokenData);

					//start timer again in 10 minutes or 30 seconds (depending on configuration param) and set time in local storage
					this.setLastTimeChangedSecondaryTokenExpiration(
						new Date(
							currentDate.getTime() +
								THEME_SETTINGS.keepAliveInterval * 1000
						).toISOString()
					);
				}
			}
		}
	};

	@action.bound
	async sendWelcomeUseBonusConfirmation(isBonusDeclined: boolean) {
		const shouldOfferBonus = this.isPrimaryUserOnline
			? this.user?.shouldOfferBonus
			: this.secondaryUser?.shouldOfferBonus;

		const bonusDeclineStatus = this.isPrimaryUserOnline
			? this.user?.isBonusDeclined
			: this.secondaryUser?.isBonusDeclined;

		if (!this.isInternetUser || !shouldOfferBonus || bonusDeclineStatus) {
			return;
		}

		await this.sendUseBonusConfirmation(isBonusDeclined);
	}

	@action.bound
	async sendMyBonusesUseBonusConfirmation(isBonusDeclined: boolean) {
		if (!this.isInternetUser) {
			return;
		}

		await this.sendUseBonusConfirmation(isBonusDeclined);
	}

	@action.bound
	async sendUseBonusConfirmation(isBonusDeclined: boolean) {
		try {
			const responseData =
				await MarketplaceBonusEligibilityApiService.useMarketplaceBonus(
					isBonusDeclined,
					createApiRequestOptions(
						RequestContextType.Wallet,
						this.activeWalletId
					)
				);

			if (
				!isBonusDeclined &&
				responseData != null &&
				responseData.bonusStatusEnum === BonusStatusEnum.CanGetBonus &&
				responseData.isSuccess
			) {
				this.rootStore.notificationStore.success(
					localizationService.t(
						"my-bonuses:MARKETPLACE_BONUS.SUCCESS_MESSAGE"
					)
				);
				this.setShouldOfferBonusFlag(false);
			}
		} catch (error) {
			console.error(error);
			if (error?.data?.errorCode != null) {
				switch (error.data.errorCode) {
					case 400149:
						this.rootStore.notificationStore.error(
							localizationService.t(
								"error-handling:INVALID_USER"
							)
						);
						break;
					case 400185:
						this.rootStore.notificationStore.error(
							localizationService.t(
								"ACCOUNT_VERIFICATION.USER_ACCOUNT_NOT_VERIFIED_HEADER"
							)
						);
						break;
					case 400219:
						this.rootStore.notificationStore.error(
							localizationService.t(
								"my-bonuses:MARKETPLACE_BONUS.NOT_ONLINE_PLAYER"
							)
						);
						break;
					case 400234:
						this.rootStore.notificationStore.error(
							localizationService.t("my-bonuses:NO_ACTIVE")
						);
						break;
					case 400235:
						this.rootStore.notificationStore.error(
							localizationService.t(
								"my-bonuses:VOUCHER_BONUS.BONUS_ALREADY_USED"
							)
						);
						break;
					case 4040020000:
					default:
						this.rootStore.notificationStore.error(
							localizationService.t(
								"error-handling:GENERIC_ERROR"
							)
						);
				}
				this.setShouldOfferBonusFlag(false);
			} else {
				this.rootStore.notificationStore.error(
					localizationService.t(
						"error-handling:GENERIC_ERROR"
					)
				);
			}
		}
	}

	// #endregion BONUS

	async keepAlive(options: ContextOptions): Promise<void> {
		if (!this.isLoggedIn) return;
		try {
			await KeepAliveApiService.ping(options);
		} catch (err) {
			console.error(err);
		}
	}

	//#region resolvers

	@action.bound
	resolveBettingActivity(): void {
		if (this.user == null) {
			return;
		}
		this.user.bettingActivityNeeded = false;
		if (this.user == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.USER_KEY
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.USER_KEY,
				JSON.stringify(this.user)
			);
		}
	}

	@action.bound
	resolveAcceptNewTerms(): void {
		if (this.user == null) {
			return;
		}
		this.user.shouldAcceptTermsAndConditions = false;
		if (this.user == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.USER_KEY
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.USER_KEY,
				JSON.stringify(this.user)
			);
		}
	}

	@action.bound
	resolveUserEmail(newEmail: string): void {
		if (this.user == null) {
			return;
		}
		//set new email and set approve flag to true so we can render correctly email fields
		this.user.email = newEmail;
		this.user.isOfflineUserMailApproved = true;
		if (this.user == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.USER_KEY
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.USER_KEY,
				JSON.stringify(this.user)
			);
		}
	}

	@action.bound
	resolveBalance(): void {
		this.refreshUserBalance();
	}

	//#endregion resolvers

	@action.bound
	async refreshLoggedInUser() {
		if (this.user == null) {
			return;
		}

		let primaryUserResponse: LoginResponseUser | null = null;
		try {
			const service = await UserService;
			primaryUserResponse = await service.default.refreshUser(
				createApiRequestOptions(RequestContextType.Default)
			);
		} catch (error) {
			console.error("Failed to refresh primary user:", error);

			this.setUser(null);
			return;
		}

		if (primaryUserResponse) {
			primaryUserResponse.isInternetUser = this.user.isInternetUser;

			primaryUserResponse.accountVerificationStatus =
				primaryUserResponse.accountVerificationStatus;

			this.setUser({
				...primaryUserResponse,
				userContextType: RequestContextType.Default,
			});
		} else {
			this.setUser(null);
		}

		if (this.secondaryUser != null) {
			const secondaryContext = this.secondaryUser.isInternetUser
				? RequestContextType.Online
				: RequestContextType.Shop;

			let secondaryUserResponse: LoginResponseUser | null = null;
			try {
				const service = await UserService;
				secondaryUserResponse = await service.default.refreshUser(
					createApiRequestOptions(secondaryContext)
				);
			} catch (error) {
				console.error("Failed to refresh secondary user:", error);
				this.setSecondaryUser(null);
			}

			if (secondaryUserResponse) {
				secondaryUserResponse.isInternetUser =
					this.secondaryUser.isInternetUser;

				secondaryUserResponse.accountVerificationStatus =
					secondaryUserResponse.accountVerificationStatus;

				this.setSecondaryUser({
					...secondaryUserResponse,
					userContextType: secondaryContext,
				});
			} else {
				this.setSecondaryUser(null);
			}
		}
	}

	//#region setters

	@action.bound
	async setUser(user: User | null): Promise<void> {
		this.user = user;

		if (this.user?.bettingActivityNeeded) {
			await this.bettingActivityStore.fetchBettingActivityData();

			if (
				this.bettingActivityStore.bettingActivityFetchedData
					?.bettingActivity == null &&
				this.bettingActivityStore.bettingActivityFetchedData
					?.userActivity == null
			) {
				this.resolveBettingActivity();
				await this.bettingActivityStore.updateBettingActivityReview();
			}
		}
		if (this.user == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.USER_KEY
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.USER_KEY,
				JSON.stringify(this.user)
			);
		}
	}

	@action.bound
	setToken(value: UserToken | null): void {
		this.token = value;
		if (this.token == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.USER_TOKEN
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.USER_TOKEN,
				JSON.stringify(this.token)
			);
		}
	}

	@action.bound
	setSecondaryUser(user: User | null | undefined): void {
		this.secondaryUser = user || null;
		if (this.secondaryUser == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.SECONDARY_USER
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.SECONDARY_USER,
				JSON.stringify(this.secondaryUser)
			);
		}
	}

	@action.bound
	setSecondaryToken(secondaryToken: UserToken | null): void {
		this.secondaryToken = secondaryToken;
		if (this.secondaryToken == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.SECONDARY_TOKEN
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.SECONDARY_TOKEN,
				JSON.stringify(this.secondaryToken)
			);
		}
	}

	@action.bound
	setAuthenticationToken(
		accountActivation: AccountActivation | null | undefined
	): void {
		this.accountActivation = accountActivation || null;
		if (this.accountActivation == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.ACCOUNT_ACTIVATION
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.ACCOUNT_ACTIVATION,
				JSON.stringify(this.accountActivation)
			);
		}
	}

	@action.bound
	setActiveUserAccountTypeId(id: string | null): void {
		this.activeAccountTypeId = id;
		if (this.activeAccountTypeId == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.ACC
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.ACC,
				this.activeAccountTypeId
			);
		}
	}

	@action.bound
	setActiveWalletId(id: string | null): void {
		this.activeWalletId = id;
		if (this.activeWalletId == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.WALLET_ID
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.WALLET_ID,
				this.activeWalletId
			);
		}
	}

	@action.bound
	setRealityCheckTime(value: number | null) {
		this.realityCheckTime = value;
		if (this.realityCheckTime == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.REALITY_CHECK_TOTAL_TIME
			);
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.REALITY_CHECK_EXPIRATION
			);
		} else if (!THEME_SETTINGS.isRealityCheckWithHeaderEnabled) {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.REALITY_CHECK_EXPIRATION,
				JSON.stringify(this.realityCheckTime)
			);
		}
		this.rootStore.realityCheckStore.onRealityCheckUpdate();
	}

	@action.bound
	setAutoLogoutPeriod(value: typeof this.autoLogoutPeriod) {
		this.autoLogoutPeriod = value;
		if (this.autoLogoutPeriod == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.AUTO_LOGOUT_PERIOD
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.AUTO_LOGOUT_PERIOD,
				JSON.stringify(this.autoLogoutPeriod)
			);
		}
	}

	removeAccountStatementFilter() {
		this.rootStore.localStorageProvider.remove(
			StorageStateKeysEnum.ACCOUNT_STATEMENT_FILTER
		);
	}

	removeMyBetsFilter() {
		this.rootStore.localStorageProvider.remove(
			StorageStateKeysEnum.MY_BETS_FILTER
		);
	}
	/**
	 * Used to track is token expired.
	 * @param value ISO Time string
	 */
	@action.bound
	setLastTimeChangedTokenExpiration(value?: string | null): void {
		if (value != null) {
			this.lastTimeChangedTokenExpiration = value;
		} else {
			this.lastTimeChangedTokenExpiration = null;
		}
		if (this.lastTimeChangedTokenExpiration == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.TOKEN_EXPIRATION_LAST_CHANGE
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.TOKEN_EXPIRATION_LAST_CHANGE,
				this.lastTimeChangedTokenExpiration
			);
		}
	}
	@action.bound
	setLastTimeChangedSecondaryTokenExpiration(value?: string | null): void {
		if (value != null) {
			this.lastTimeChangedSecondaryTokenExpiration = value;
		} else {
			this.lastTimeChangedSecondaryTokenExpiration = null;
		}

		if (this.lastTimeChangedSecondaryTokenExpiration == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.SECONDARY_TOKEN_EXPIRATION_DATE
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.SECONDARY_TOKEN_EXPIRATION_DATE,
				this.lastTimeChangedSecondaryTokenExpiration
			);
		}
	}

	@action.bound
	setTokenExpirationDate(expirationDate: string): void {
		const newUserToken: UserToken = {
			...(this.token as UserToken),
			expirationDate: expirationDate,
		};
		this.setToken(newUserToken);
	}

	@action.bound
	setLoginHistory(value: LoginHistoryResponseModel | null): void {
		this.loginHistory = value;
		if (this.loginHistory == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.LOGIN_HISTORY
			);
		} else {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.LOGIN_HISTORY,
				JSON.stringify(this.loginHistory)
			);
		}
	}

	private async getValidatedAccounts(
		accounts: UserAccount[] | null
	): Promise<UserAccount[] | null> {
		// Check for old structure (missing shopId).
		if (accounts?.some((wallet) => !wallet.shopId)) {
			try {
				const api = await UserService;
				return await retryRequest(
					() => api.default.getUserAccounts(),
					2
				);
			} catch (e) {
				console.error("Failed to fetch updated user accounts:", e);
				return [];
			}
		}
		return accounts;
	}

	private updateActiveShopIdIfNeeded() {
		if (this.activeShopUserShopId || !this.userAccounts) {
			return;
		}

		const activeShopWallet = this.userAccounts.find(
			(wallet) =>
				wallet.isCurrentlyActive && RETAIL_USER_WALLETS.has(wallet.abrv)
		);

		if (activeShopWallet) {
			this.setActiveShopUserShopId(activeShopWallet.shopId);
		} else if (this.shopUser?.shopId) {
			this.setActiveShopUserShopId(this.shopUser.shopId);
		}
	}

	private updateActiveUserAccount() {
		if (
			this.activeWalletId &&
			this.userAccounts
				?.filter((ua) => BETTING_USER_WALLETS.has(ua.abrv))
				.some(
					(ua) =>
						ua.id === this.activeWalletId &&
						!isWalletDisabledForBetting(ua)
				)
		) {
			return;
		}

		const newActiveAccount = this.findValidActiveAccount();
		if (newActiveAccount) {
			this.setActiveWalletId(newActiveAccount.id);
			if (RETAIL_USER_WALLETS.has(newActiveAccount.abrv)) {
				this.setActiveShopUserShopId(newActiveAccount.shopId);
			}
		}
	}
	private findValidActiveAccount(): UserAccount | null {
		if (!this.userAccounts) return null;

		if (THEME_SETTINGS.defaultAccount) {
			const defaultAccount = this.userAccounts.find(
				(ua) => ua.abrv === THEME_SETTINGS.defaultAccount
			);
			if (defaultAccount) return defaultAccount;
		}

		if (this.onlineUser) {
			const validWallet = this.onlineUserWallets
				.filter((w) => BETTING_USER_WALLETS.has(w.abrv))
				.find(
					(w) =>
						!isWalletDisabledForBetting(w) &&
						(!THEME_SETTINGS.isDKMW || w.isPrimary)
				);
			if (validWallet) return validWallet;
		}
		if (this.shopUser) {
			const shopWallets = this.shopUserWallets
				.filter((w) => w.shopId === this.activeShopUserShopId)
				.filter((w) => BETTING_USER_WALLETS.has(w.abrv));
			const sortedShopWallets = this.sortUserAccounts(shopWallets);
			const validShopWallet = sortedShopWallets.find(
				(w) => !isWalletDisabledForBetting(w)
			);
			if (validShopWallet) return validShopWallet;
		}

		return (
			this.userAccounts
				.filter((w) => BETTING_USER_WALLETS.has(w.abrv))
				.find((w) => !isWalletDisabledForBetting(w)) ||
			this.userAccounts[0]
		);
	}

	@action.bound
	async setUserAccounts(value: UserAccount[] | null) {
		if (!value) {
			this.userAccounts = null;
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.USER_ACCOUNTS
			);
			return;
		}
		const validatedAccounts = await this.getValidatedAccounts(value);
		if (!validatedAccounts?.length) {
			this.userAccounts = null;
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.USER_ACCOUNTS
			);
			return;
		}

		this.userAccounts = this.sortUserAccounts(validatedAccounts);
		this.updateActiveShopIdIfNeeded();
		this.updateActiveUserAccount();

		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.USER_ACCOUNTS,
			JSON.stringify(this.userAccounts)
		);
	}

	@action.bound
	setShouldOfferBonusFlag(flagValue: boolean) {
		if (this.user != null) {
			this.setUser({ ...this.user, shouldOfferBonus: flagValue });
		}
	}

	@action.bound
	setWalletGracePeriod(expirationDate: string): void {
		if (this.user) {
			this.user.userAccountDeactivationGracePeriodExpirationDate =
				expirationDate;
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.USER_KEY,
				JSON.stringify(this.user)
			);
		}
		if (this.secondaryUser) {
			this.secondaryUser.userAccountDeactivationGracePeriodExpirationDate =
				expirationDate;
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.SECONDARY_USER,
				JSON.stringify(this.secondaryUser)
			);
		}
	}

	@action.bound
	clearWalletGracePeriod(): void {
		if (this.user) {
			this.user.userAccountDeactivationGracePeriodExpirationDate = null;
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.USER_KEY,
				JSON.stringify(this.user)
			);
		}
		if (this.secondaryUser) {
			this.secondaryUser.userAccountDeactivationGracePeriodExpirationDate =
				null;
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.SECONDARY_USER,
				JSON.stringify(this.secondaryUser)
			);
		}
	}

	@action.bound
	setActiveShopUserShopId(shopId: string | null) {
		if (shopId == this.activeShopUserShopId) {
			return;
		}
		const previousShopId = this.activeShopUserShopId;
		this.activeShopUserShopId = shopId;

		if (this.activeShopUserShopId == null) {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.ACTIVE_SHOP_USER_SHOP_ID
			);
			return;
		}

		const previousWallet = this.shopUserShops
			.get(previousShopId || "")
			?.wallets.find((wallet) => wallet.id == this.activeWalletId);
		if (previousWallet) {
			const shop = this.shopUserShops?.get(this.activeShopUserShopId);
			let isWalletChanged = false;
			for (let wallet of shop?.wallets || []) {
				if (isWalletDisabledForBetting(wallet)) {
					continue;
				}
				if (wallet.abrv == previousWallet.abrv) {
					this.setActiveWalletId(wallet.id);
					isWalletChanged = true;
					break;
				}
			}
			if (!isWalletChanged) {
				// in case if there is no similar wallet take first valid available
				for (let wallet of shop?.wallets || []) {
					if (isWalletDisabledForBetting(wallet)) {
						continue;
					}
					this.setActiveWalletId(wallet.id);
					break;
				}
			}
		}

		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.ACTIVE_SHOP_USER_SHOP_ID,
			JSON.stringify(this.activeShopUserShopId)
		);
	}

	//#endregion setters

	//#region helpers

	getUserAccountType(
		activeAccount: AccountTypes = AccountTypes.OFFLINE
	): UserTypes {
		if (this.secondaryUser == null) {
			return UserTypes.PRIMARY;
		}

		if (
			(this.isPrimaryUserOnline &&
				activeAccount === AccountTypes.OFFLINE) ||
			(!this.isPrimaryUserOnline && activeAccount === AccountTypes.ONLINE)
		) {
			return UserTypes.SECONDARY;
		}

		return UserTypes.PRIMARY;
	}

	getWalletOwnerByAccountTypeId(activeAccountId?: string | null): UserTypes {
		let activeWallet = this.userAccounts?.find(
			(wallet) => wallet.accountTypeId === activeAccountId
		);
		if (
			activeAccountId == null ||
			activeWallet?.userId === this?.user?.id
		) {
			return UserTypes.PRIMARY;
		}
		return UserTypes.SECONDARY;
	}

	getWalletOwnerByWalletId(walletId?: string | null): UserTypes {
		let activeWallet = this.userAccounts?.find(
			(wallet) => wallet.id === walletId
		);
		if (activeWallet == null || activeWallet?.userId === this?.user?.id) {
			return UserTypes.PRIMARY;
		}
		return UserTypes.SECONDARY;
	}

	mapUserIdToUserContext(userId: string | null) {
		if (this.user?.id === userId) {
			return createApiRequestOptions(RequestContextType.Default);
		}

		if (this.secondaryUser?.id === userId) {
			return this.secondaryUser.isInternetUser
				? createApiRequestOptions(RequestContextType.Online)
				: createApiRequestOptions(RequestContextType.Shop);
		}

		return createApiRequestOptions(RequestContextType.Default);
	}
	//#endregion helpers

	//#region reading storage

	@action.bound
	readFromLocalStorage = async (key: string | null) => {
		if (key == null || key == "userToken") {
			this.setToken(
				JSON.parse(
					this.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.USER_TOKEN
					) as string
				) as LoginResponseToken | null
			);
		}

		if (key == null || key == "user") {
			this.setUser(
				JSON.parse(
					this.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.USER_KEY
					) as string
				) as User | null
			);
		}

		if (key == null || key == "secondaryUser") {
			this.setSecondaryUser(
				JSON.parse(
					this.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.SECONDARY_USER
					) as string
				) as User | null
			);
		}

		if (key == null || key == "secondaryToken") {
			this.setSecondaryToken(
				JSON.parse(
					this.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.SECONDARY_TOKEN
					) as string
				) as LoginResponseToken | null
			);
		}

		if (key == null || key == "accountActivation") {
			this.setAuthenticationToken(
				JSON.parse(
					this.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.ACCOUNT_ACTIVATION
					) as string
				) as AccountActivation | null
			);
		}

		if (key == null || key == "loginHistory") {
			this.setLoginHistory(
				JSON.parse(
					this.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.LOGIN_HISTORY
					) as string
				) as LoginHistoryResponseModel | null
			);
		}
		if (key == null || key == "acc") {
			this.setActiveUserAccountTypeId(
				this.rootStore.localStorageProvider.get(
					StorageStateKeysEnum.ACC
				) as string
			);
		}
		if (key == null || key == StorageStateKeysEnum.WALLET_ID) {
			this.setActiveWalletId(
				this.rootStore.localStorageProvider.get(
					StorageStateKeysEnum.WALLET_ID
				) as string
			);
		}

		if (
			key == null ||
			key == StorageStateKeysEnum.ACTIVE_SHOP_USER_SHOP_ID
		) {
			this.setActiveShopUserShopId(
				JSON.parse(
					this.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.ACTIVE_SHOP_USER_SHOP_ID
					) as string
				) as string
			);
		}

		if (key == null || key == "userAccounts") {
			await this.setUserAccounts(
				JSON.parse(
					this.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.USER_ACCOUNTS
					) as string
				) as UserAccount[] | null
			);
		}

		if (key == null || key == "token-expiration-last-change") {
			this.setLastTimeChangedTokenExpiration(
				this.rootStore.localStorageProvider.get(
					StorageStateKeysEnum.TOKEN_EXPIRATION_LAST_CHANGE
				) as string
			);
		}

		if (key == null || key == "secondary-token-expiration-date") {
			this.setLastTimeChangedSecondaryTokenExpiration(
				this.rootStore.localStorageProvider.get(
					StorageStateKeysEnum.SECONDARY_TOKEN_EXPIRATION_DATE
				) as string
			);
		}

		if (key == null || key == "reality-check-expiration") {
			this.setRealityCheckTime(
				JSON.parse(
					this.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.REALITY_CHECK_EXPIRATION
					) as string
				) as number
			);
		}

		if (key == null || key == "auto-logout-period") {
			this.setAutoLogoutPeriod(
				JSON.parse(
					this.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.AUTO_LOGOUT_PERIOD
					) as string
				) as number
			);
		}
	};

	storageActionListener = (e: StorageEvent) => {
		const hadUser = this.isLoggedIn;
		this.readFromLocalStorage(e.key);

		if (!hadUser && this.isLoggedIn) {
			this.rootStore.cashoutHubStore.initializeCashoutHub();
			this.rootStore.userHubStore.initializeUserHub();
		}
	};

	//#region reading storage

	//#region disposers

	@action.bound
	onDispose() {
		window.removeEventListener("storage", this.storageActionListener);
		clearInterval(this.keepAliveInterval);
		this.loginStore.onDispose();
	}

	//#endregion disposers
}

//

export type User = LoginResponseUser & { userContextType: RequestContextType };

export type UserToken = LoginResponseToken;

export type AccountLockInfo = {
	duration: number;
	startDate: DateTime;
	endDate: DateTime;
};

export type BettingTimeoutInfo = {
	duration: number | null;
	endDate: DateTime | null;
};
export type AccountActivation = LoginActivationResponseDto;
